import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, ImageIcon, X, Save, Eye, EyeOff, GripVertical, ImageOff, Link as LinkIcon } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import UploadButton from './UploadButton';
import RichTextEditor from './RichTextEditor';

/**
 * Generic CRUD editor for content collections.
 * Receives a `name` (collection key) and `schema` describing fields.
 *
 * schema field: { key, label, type: 'text'|'textarea'|'image'|'number'|'bool'|'url', help?, required? }
 */
export default function ContentEditor({ name, title, kicker, schema, defaultData = {}, hideHeader = false, preview = null }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // { id?, data, sort_order, published }
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    api.adminListContent(name)
      .then(setItems).catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [name]);

  const startNew = () => setEditing({ data: { ...defaultData }, sort_order: items.length, published: true });
  const startEdit = (it) => setEditing({ id: it.id, data: { ...it.data }, sort_order: it.sort_order || 0, published: it.published !== false });

  // ESC closes modal
  useEffect(() => {
    if (!editing) return;
    const onKey = (e) => { if (e.key === 'Escape') setEditing(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing]);

  const save = async () => {
    try {
      // Auto-generate slug if missing (needed for project/event detail URLs)
      if (editing.data && editing.data.title && !editing.data.slug) {
        editing.data.slug = slugifyTR(editing.data.title);
      }
      if (editing.id) {
        await api.adminUpdateContent(name, editing.id, editing.data, editing.sort_order, editing.published);
        toast.success('Güncellendi');
      } else {
        await api.adminCreateContent(name, editing.data, editing.sort_order);
        toast.success('Eklendi');
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    }
  };

  const slugifyTR = (s) => (s || '').toLowerCase()
    .replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);

  const del = async (id) => {
    if (!window.confirm('Silmek istediğinizden emin misiniz?')) return;
    await api.adminDeleteContent(name, id);
    toast.success('Silindi');
    reload();
  };

  // ---- Drag & drop reorder ----
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== dragOverId) setDragOverId(id);
  };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { onDragEnd(); return; }
    const fromIdx = items.findIndex(x => x.id === dragId);
    const toIdx = items.findIndex(x => x.id === targetId);
    if (fromIdx < 0 || toIdx < 0) { onDragEnd(); return; }
    const next = [...items];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // Reassign sort_order based on new index
    const reindexed = next.map((it, i) => ({ ...it, sort_order: i }));
    setItems(reindexed);
    onDragEnd();
    // Persist (only items whose sort_order actually changed)
    setReordering(true);
    try {
      const changed = reindexed.filter((it, i) => (items.find(x => x.id === it.id)?.sort_order ?? -1) !== i);
      await Promise.all(changed.map(it => api.adminUpdateContent(name, it.id, it.data, it.sort_order, it.published !== false)));
      toast.success('Sıralama güncellendi');
    } catch {
      toast.error('Sıralama kaydedilemedi');
      reload(); // revert
    } finally { setReordering(false); }
  };

  return (
    <div data-testid={`content-${name}`}>
      {hideHeader ? (
        <div className="flex items-center justify-between mb-4">
          <p className="text-ink-400 text-sm">{items.length} kayıt</p>
          <button onClick={startNew} data-testid={`new-${name}`} className="bg-ink-600 text-cream rounded-full px-5 h-11 text-sm font-semibold hover:bg-ink-500 inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Yeni Ekle
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">{kicker}</div>
            <h1 className="font-display text-4xl mt-2 leading-none">{title}</h1>
            <p className="mt-2 text-ink-400 text-sm">{items.length} kayıt</p>
          </div>
          <button onClick={startNew} data-testid={`new-${name}`} className="bg-ink-600 text-cream rounded-full px-5 h-11 text-sm font-semibold hover:bg-ink-500 inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Yeni Ekle
          </button>
        </div>
      )}

      {loading && <div className="bg-white rounded-2xl p-8 text-center text-ink-400 border border-ink-100">Yükleniyor...</div>}

      {!loading && items.length === 0 && (
        <div className="bg-white rounded-2xl p-12 text-center border border-ink-100">
          <p className="text-ink-400 mb-4">Henüz kayıt yok.</p>
          <button onClick={startNew} className="bg-gold text-ink-700 rounded-full px-5 h-11 text-sm font-semibold hover:bg-gold-400 inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> İlk kaydı ekle
          </button>
        </div>
      )}

      {/* Cards grid */}
      {reordering && <div className="mb-3 text-xs text-ink-400">Sıralama kaydediliyor...</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(it => {
          const img = (it.data && (it.data.image || it.data.logo || it.data.cover || it.data.photo || it.data.thumbnail)) || null;
          const linkUrl = (it.data && (it.data.website || it.data.url || it.data.pdf_url || it.data.file || it.data.link)) || null;
          const linkLabel = linkUrl ? (linkUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/^\/+/, '').split(/[?/]/)[0] || 'link') : null;
          const titleField = (it.data && (it.data.name || it.data.title || it.data.question || it.data.bank || it.data.label || it.data.outlet)) || '(başlıksız)';
          const sub = (it.data && (it.data.role || it.data.subtitle || it.data.category || it.data.iban || it.data.branch)) || '';
          const isOver = dragOverId === it.id && dragId !== it.id;
          const isDragging = dragId === it.id;
          return (
            <div
              key={it.id}
              draggable
              onDragStart={(e) => onDragStart(e, it.id)}
              onDragOver={(e) => onDragOver(e, it.id)}
              onDrop={(e) => onDrop(e, it.id)}
              onDragEnd={onDragEnd}
              className={`group bg-white rounded-2xl border overflow-hidden transition-all ${isOver ? 'border-gold ring-2 ring-gold/40 -translate-y-0.5' : 'border-ink-100 hover:border-gold'} ${isDragging ? 'opacity-40' : ''}`}
              style={{ cursor: 'grab' }}
            >
              {/* Preview strip — visible for every card, even when no image */}
              <div className="aspect-[4/3] overflow-hidden bg-ink-50 relative">
                {img ? (
                  <img
                    src={img}
                    alt=""
                    className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-ink-300 gap-2">
                    <ImageOff className="w-8 h-8" />
                    <span className="text-[10px] uppercase tracking-widest font-bold">Görsel yok</span>
                  </div>
                )}
                <div className="absolute top-2 left-2 w-7 h-7 grid place-items-center rounded-md bg-ink-900/60 text-cream backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity" title="Sürükleyerek sıralayın">
                  <GripVertical className="w-3.5 h-3.5" />
                </div>
                {/* Status chips (top-right) — show at a glance what's set */}
                <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                  {linkUrl && (
                    <a
                      href={linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title={linkUrl}
                      className="inline-flex items-center gap-1 text-[10px] font-bold rounded-full bg-emerald-500/95 text-white px-2 py-1 hover:bg-emerald-600 backdrop-blur-sm max-w-[180px]"
                    >
                      <LinkIcon className="w-3 h-3 shrink-0" />
                      <span className="truncate">{linkLabel}</span>
                    </a>
                  )}
                  {!img && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold rounded-full bg-coral-300/95 text-white px-2 py-1">
                      <ImageOff className="w-3 h-3" /> Görsel eksik
                    </span>
                  )}
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg leading-snug text-ink-600 truncate" title={titleField}>{titleField}</h3>
                    {sub && <div className="text-xs text-ink-400 mt-1 truncate">{sub}</div>}
                  </div>
                  {it.published === false && <span className="px-2 py-0.5 text-[10px] rounded-full bg-coral-50 text-coral-300 font-bold uppercase tracking-widest">Taslak</span>}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={() => startEdit(it)} data-testid={`edit-${name}-${it.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-ink-600 text-cream rounded-lg px-3 h-9 text-xs font-semibold hover:bg-ink-500"><Edit3 className="w-3.5 h-3.5" /> Düzenle</button>
                  <button onClick={() => del(it.id)} className="w-9 h-9 grid place-items-center rounded-lg bg-coral-50 text-coral-300 hover:bg-coral-100"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="mt-2 text-[10px] text-ink-400 tabular">Sıra: {it.sort_order || 0}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-ink-900/70 backdrop-blur-sm grid place-items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-ink-100 flex items-center justify-between rounded-t-2xl">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">{kicker || 'İçerik'}</div>
                <h2 className="font-display text-2xl mt-0.5">{editing.id ? 'Düzenle' : 'Yeni Kayıt'}</h2>
              </div>
              <button onClick={() => setEditing(null)} className="w-9 h-9 grid place-items-center hover:bg-cream-200 rounded-lg" title="Kapat (Esc)"><X className="w-4 h-4" /></button>
            </div>
            <div className={`p-6 space-y-5 ${preview ? 'grid lg:grid-cols-[1fr_440px] gap-6 space-y-0' : ''}`}>
              <div className={preview ? 'space-y-5 overflow-y-auto max-h-[70vh] pr-2' : ''}>
              {schema.map(field => (
                <div key={field.key}>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold">{field.label}{field.required && <span className="text-coral-300"> *</span>}</label>
                  {field.help && <div className="text-xs text-ink-400 mt-0.5">{field.help}</div>}
                  {field.type === 'textarea' && (
                    <textarea
                      value={editing.data[field.key] || ''}
                      onChange={e => setEditing(s => ({ ...s, data: { ...s.data, [field.key]: e.target.value } }))}
                      rows={4}
                      className="mt-2 w-full bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 py-2.5 text-sm outline-none"
                    />
                  )}
                  {field.type === 'image' && (
                    <div className="mt-2">
                      <div className="flex gap-2">
                        <input
                          value={editing.data[field.key] || ''}
                          onChange={e => setEditing(s => ({ ...s, data: { ...s.data, [field.key]: e.target.value } }))}
                          placeholder="https:// veya /uploads/..."
                          className="flex-1 bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-10 text-sm outline-none"
                        />
                        <UploadButton onUploaded={url => setEditing(s => ({ ...s, data: { ...s.data, [field.key]: url } }))} />
                      </div>
                      {editing.data[field.key] && (
                        <img src={editing.data[field.key]} alt="" className="mt-2 max-h-40 rounded-lg border border-ink-100" />
                      )}
                    </div>
                  )}
                  {field.type === 'bool' && (
                    <label className="mt-2 inline-flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!editing.data[field.key]} onChange={e => setEditing(s => ({ ...s, data: { ...s.data, [field.key]: e.target.checked } }))} />
                      <span className="text-sm">Aktif</span>
                    </label>
                  )}
                  {field.type === 'number' && (
                    <input type="number" value={editing.data[field.key] || 0} onChange={e => setEditing(s => ({ ...s, data: { ...s.data, [field.key]: parseInt(e.target.value) || 0 } }))} className="mt-2 w-full bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-10 text-sm outline-none" />
                  )}
                  {field.type === 'richtext' && (
                    <div className="mt-2">
                      <RichTextEditor
                        value={editing.data[field.key] || ''}
                        onChange={(html) => setEditing(s => ({ ...s, data: { ...s.data, [field.key]: html } }))}
                        placeholder={field.placeholder || 'İçerik yazmaya başlayın... Başlık, liste, alıntı, görsel ekleyebilirsiniz.'}
                        minHeight={300}
                      />
                    </div>
                  )}
                  {field.type === 'select' && (
                    <select
                      value={editing.data[field.key] || ''}
                      onChange={e => setEditing(s => ({ ...s, data: { ...s.data, [field.key]: e.target.value } }))}
                      className="mt-2 w-full bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-10 text-sm outline-none"
                    >
                      <option value="">— Seçin —</option>
                      {(field.options || []).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                  {field.type === 'file' && (
                    <div className="mt-2">
                      <div className="flex gap-2">
                        <input
                          value={editing.data[field.key] || ''}
                          onChange={e => setEditing(s => ({ ...s, data: { ...s.data, [field.key]: e.target.value } }))}
                          placeholder="https:// veya /uploads/... (PDF)"
                          className="flex-1 bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-10 text-sm outline-none"
                        />
                        <UploadButton
                          accept={field.accept || 'application/pdf,.pdf,.doc,.docx'}
                          label={field.uploadLabel || 'PDF Yükle'}
                          onUploaded={url => setEditing(s => ({ ...s, data: { ...s.data, [field.key]: url } }))}
                        />
                      </div>
                      {editing.data[field.key] && (
                        <a href={editing.data[field.key]} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs text-ink-500 hover:text-ink-700 underline">
                          📄 Dosyayı görüntüle
                        </a>
                      )}
                    </div>
                  )}
                  {(!['textarea', 'image', 'file', 'bool', 'number', 'select', 'richtext'].includes(field.type)) && (
                    <input
                      value={editing.data[field.key] || ''}
                      onChange={e => setEditing(s => ({ ...s, data: { ...s.data, [field.key]: e.target.value } }))}
                      placeholder={field.placeholder}
                      list={field.suggestions ? `sugg-${field.key}` : undefined}
                      className="mt-2 w-full bg-cream-200 border border-ink-100 focus:border-ink-500 rounded-xl px-3 h-10 text-sm outline-none"
                    />
                  )}
                  {field.suggestions && (
                    <datalist id={`sugg-${field.key}`}>
                      {field.suggestions.map(s => <option key={s} value={s} />)}
                    </datalist>
                  )}
                </div>
              ))}

              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-ink-100">
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold">Sıralama</label>
                  <input type="number" value={editing.sort_order} onChange={e => setEditing(s => ({ ...s, sort_order: parseInt(e.target.value) || 0 }))} className="mt-2 w-full bg-cream-200 border border-ink-100 rounded-xl px-3 h-10 text-sm outline-none" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold">Yayında</label>
                  <button
                    onClick={() => setEditing(s => ({ ...s, published: !s.published }))}
                    className={`mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl px-3 h-10 text-sm font-semibold ${editing.published ? 'bg-emerald-50 text-emerald-700' : 'bg-coral-50 text-coral-300'}`}
                  >
                    {editing.published ? <><Eye className="w-4 h-4" /> Yayında</> : <><EyeOff className="w-4 h-4" /> Taslak</>}
                  </button>
                </div>
              </div>
              </div>
              {preview && (
                <div className="bg-cream-200 rounded-2xl p-4 lg:sticky lg:top-4 lg:self-start max-h-[70vh] overflow-y-auto">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold mb-3">Canlı Önizleme</div>
                  {preview(editing.data)}
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-ink-100 px-6 py-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 h-11 rounded-full border border-ink-200 text-ink-500 hover:bg-cream-200 text-sm font-semibold">İptal</button>
              <button onClick={save} data-testid={`save-${name}`} className="bg-gold text-ink-700 rounded-full px-5 h-11 text-sm font-semibold hover:bg-gold-400 inline-flex items-center gap-2"><Save className="w-4 h-4" /> Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
